import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMundusStore } from '../stores/mundus.store';
import UpdateMetricModal from '../components/UpdateMetricModal';
import Chat from '../components/Chat';
import WinsPanel from '../components/WinsPanel';
import { DailyReports } from '../components/DailyReports';
import { Skeleton } from '../components/Skeleton';
import { getAchievementProgress } from '../lib/achievements.api';
import { AchievementProgress as AchievementProgressType } from '../types/mundus';

export default function MundusDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentMundus, fetchMundusById, isLoading } = useMundusStore();
  const [selectedMetric, setSelectedMetric] = useState<any>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [, setAchievementProgress] = useState<AchievementProgressType | null>(null);

  useEffect(() => {
    if (id) {
      fetchMundusById(id);
      loadAchievementProgress(id);
    }
  }, [id, fetchMundusById]);

  const loadAchievementProgress = async (mundusId: string) => {
    try {
      const progress = await getAchievementProgress(mundusId);
      setAchievementProgress(progress);
    } catch (error) {
      console.error('Error loading achievement progress:', error);
    }
  };

  const handleUpdateMetric = (metric: any) => {
    setSelectedMetric(metric);
    setIsUpdateModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    // Refresh the mundus data and achievement progress
    if (id) {
      fetchMundusById(id);
      loadAchievementProgress(id);
    }
  };

  // Suppress unused variable warnings - these may be used in future features
  void handleUpdateMetric;

  const getTemplateIcon = (goalType: string) => {
    const icons: Record<string, string> = {
      IKIGAI: '⚡',
      CLIMATE: '🌐',
      CAREER: '🚀',
    };
    return icons[goalType] || '💫';
  };

  const getTemplateColors = (goalType: string) => {
    const colors: Record<string, { border: string; text: string; bg: string; glow: string }> = {
      IKIGAI: { border: 'border-purple-300', text: 'text-purple-600', bg: 'bg-purple-50', glow: 'shadow-purple-200' },
      CLIMATE: { border: 'border-green-300', text: 'text-green-600', bg: 'bg-green-50', glow: 'shadow-green-200' },
      CAREER: { border: 'border-blue-300', text: 'text-blue-600', bg: 'bg-blue-50', glow: 'shadow-blue-200' },
    };
    return colors[goalType] || { border: 'border-blue-300', text: 'text-blue-600', bg: 'bg-blue-50', glow: 'shadow-blue-200' };
  };

  if (isLoading || !currentMundus) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header Skeleton */}
        <header className="bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Skeleton variant="text" width={120} height={16} className="mb-4" />
            <div className="flex items-center gap-4">
              <Skeleton variant="rounded" width={80} height={80} />
              <div className="flex-1">
                <Skeleton variant="text" width="40%" height={32} className="mb-2" />
                <Skeleton variant="text" width="60%" height={16} />
              </div>
              <div className="flex gap-3">
                <Skeleton variant="rounded" width={120} height={40} />
                <Skeleton variant="rounded" width={120} height={40} />
              </div>
            </div>
          </div>
        </header>
        {/* Content Skeleton */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <Skeleton variant="text" width={200} height={24} className="mb-6" />
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Skeleton variant="rounded" width={20} height={20} />
                    <Skeleton variant="text" width="80%" height={16} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const colors = getTemplateColors(currentMundus.goalType);

  return (
    <div className="min-h-screen bg-slate-50">
      <UpdateMetricModal
        metric={selectedMetric}
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        onSuccess={handleUpdateSuccess}
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-slate-600 hover:text-slate-900 flex items-center transition-colors group"
            >
              <svg className="w-4 h-4 mr-1 sm:mr-2 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`text-3xl sm:text-5xl w-14 h-14 sm:w-20 sm:h-20 rounded-xl ${colors.bg} flex items-center justify-center border-2 ${colors.border}`}>
                {getTemplateIcon(currentMundus.goalType)}
              </div>
              <div className="flex-1 sm:flex-initial">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <h1 className={`text-xl sm:text-3xl font-bold ${colors.text}`}>
                    {currentMundus.name}
                  </h1>
                  <span className={`px-2 sm:px-3 py-0.5 sm:py-1 text-xs font-medium rounded-full ${
                    currentMundus.isActive
                      ? 'bg-green-100 border border-green-300 text-green-700'
                      : 'bg-slate-100 border border-slate-300 text-slate-600'
                  }`}>
                    {currentMundus.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-slate-600 line-clamp-2">
                  {currentMundus.description || 'No description available'}
                </p>
              </div>
            </div>
            {/* Settings link */}
            <div className="sm:ml-auto">
              <button
                onClick={() => navigate('/settings')}
                className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Settings
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="space-y-4 sm:space-y-6">
          {/* Wins Progress */}
          <WinsPanel mundusId={currentMundus.id} />

          {/* Agent Chat */}
          {currentMundus.agents && currentMundus.agents.length > 0 && (
            <Chat
              mundusId={currentMundus.id}
              agents={currentMundus.agents}
              colors={colors}
            />
          )}

          {/* Mundus Visualization */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <span className={colors.text}>📊</span>
                  Mundus Visualization
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  See your progress, metrics, and agent activity in a visual overview
                </p>
              </div>
              <Link
                to={`/mundus/${currentMundus.id}/analytics`}
                className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-all bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 shadow-md hover:shadow-lg`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                View Analytics
              </Link>
            </div>
          </div>

          {/* Daily Reports */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <DailyReports mundusId={currentMundus.id} />
          </div>
        </div>
      </main>
    </div>
  );
}
